import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import {
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useTenantIdNumber } from "@/hooks/use-tenants";
import { useProjects } from "@/hooks/use-projects";
import {
  useReports,
  useGeneratePDF,
  useReportParameters,
} from "@/hooks/use-reports";
import { reportApi, EXCLUDED_PDF_PARAMS } from "@/services/report-api";
import { reportListItemApi } from "@/services/report-list-item-api";
import { buildParameterDictFromXml } from "@/hooks/use-hydrated-report-list-items";
import {
  useReportLists,
  useCreateReportList,
  useUpdateReportList,
  useDeleteReportList,
} from "@/hooks/use-report-lists";
import {
  useReportListItems,
  useCreateMultipleReportListItems,
  useUpdateReportListItem,
  useDeleteReportListItem,
  useDeleteMultipleReportListItems,
} from "@/hooks/use-report-list-items";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { useReportListSignalR } from "@/hooks/use-report-list-signalr";
import { useTenantChangeSync } from "@/hooks/use-tenant-change-sync";
import { getCachedTenantId } from "@/hooks/use-current-tenant";
import { queryKeys } from "@/hooks/query-keys";
import {
  STORAGE_KEYS,
  QUERY_PARAMS,
  EMPTY_GUID,
  DEFAULT_DELIVERY_TYPE_KEY,
  DEFAULT_FILE_TYPE_KEY,
  DEFAULT_COLLATION_TYPE_KEY,
  resolveGuid,
} from "@/lib/constants";
import { buildReportTreesByType, normalizeExpandedFoldersByType } from "@/lib/report-tree";
import {
  getParameterStringValue,
  parametersToEntries,
  collectDescendantNames,
  buildParameterDict,
} from "@/lib/parameter-helpers";
import {
  serializeParameters,
  deserializeParameters,
} from "@/lib/parameter-serializer";
import { sanitizeParameterXml } from "@/lib/parameter-sanitizer";
import { filterItemsByReportList, clearUnavailableParameterSelections } from "@/lib/report-list-helpers";
import {
  createDefaultDeliveryDto,
  createDefaultDeliveryUpdateDto,
} from "@/lib/delivery-defaults";
import type { ReportTreeItemData, ReportType } from "@/lib/report-tree";
import type { ReportList, ReportListItem } from "@/types/report-list";
import type { Report } from "@/types/report";
import type { ReportDelivery } from "@/types/delivery";
import type {
  Parameter,
  BoolParameter,
  DateTimeParameter,
  IntParameter,
  StringParameter,
  MultiSelectParameter,
} from "@/types/parameter";
import type { ContextMenuCommand } from "@/lib/context-menu-items";
import type {
  ReportListEventPayload,
  ReportListItemEventPayload,
  MultipleReportListItemsEventPayload,
} from "@/types/signalr.types";
import { cn, formatFilenameTimestamp } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-media-query";
import { Button } from "@/components/ui/button";
import { ActionBar } from "@/components/report-manager/action-bar";
import { ProfileMenu } from "@/components/report-manager/profile-menu";
import { SignalRStatus } from "@/components/report-manager/signalr-status";
import { useAppInsightsEvent } from "@/hooks/useAppInsights";
import { track } from "@/analytics";
import { ReportListSection } from "@/components/report-manager/report-list-section";
import { ReportLibrary } from "@/components/report-manager/report-library";
import { ParameterPanel } from "@/components/report-manager/parameter-panel";
import { MobileParameterSheet } from "@/components/report-manager/mobile-parameter-sheet";
import { ReportPreviewImage } from "@/components/report-manager/report-preview-image";

import {
  CreateReportListDialog,
  OpenReportListDialog,
  DuplicateReportListDialog,
  RenameReportListDialog,
  DeleteReportListDialog,
  DeliveryConfigDialog,
} from "@/components/report-manager/dialogs";

type ActiveContext = "reportList" | "reportLibrary";

/**
 * Maximum safe URL length for most browsers.
 * - Internet Explorer: 2083 chars (most restrictive)
 * - Chrome/Firefox/Safari: 8000+ chars
 * We use 2000 as a conservative threshold to support older browsers.
 */
const MAX_SAFE_URL_LENGTH = 2000;

/**
 * Checks if a URL exceeds safe length limits and returns a warning message.
 * Returns null if URL is safe.
 */
function validateUrlLength(url: string, t: (key: string, options?: any) => string): string | null {
  if (url.length > MAX_SAFE_URL_LENGTH) {
    return t("toast.warning.urlTooLong", { length: url.length, limit: MAX_SAFE_URL_LENGTH });
  }
  return null;
}

/**
 * ReportManagerPage - Main page for managing and previewing SSRS reports
 *
 * Parameter Flow for Library Preview:
 * 1. User selects report → fetch parameters with ProjectId context
 * 2. Apply context (ProjectId) to fetched parameters
 * 3. Store in activeReportParameters state
 * 4. User clicks Preview → serialize ALL parameters to JSON
 * 5. Pass JSON in URL query string (matches Blazor behavior)
 * 6. Preview page deserializes and re-fetches with complete context
 * 7. SSRS resolves cascading parameters based on all parent values
 *
 * Why pass ALL parameters:
 * Reports with cascading chains (e.g., ReportType → BatchGroup → TrussName)
 * need complete parent context. Passing only ProjectId
 * causes child parameters to receive incorrect defaults.
 *
 * Trade-off:
 * Longer URLs (may exceed 2000 chars for reports with many parameters).
 * Browser limits vary (2000-8000+ chars). URL length warning added in Phase 2.
 */
export function ReportManagerPage() {
  const { t } = useTranslation();
  // Responsive
  const isMobile = useIsMobile();

  // Auth context
  const { getUserKey } = useAuth();
  const tenantId = useTenantIdNumber();

  // Global settings
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeProjectId, setStoredProjectId] = useLocalStorage(
    STORAGE_KEYS.ACTIVE_PROJECT_ID,
    0,
  );

  // Lazily computed once on mount; a valid `projectId` param is a string of digits only.
  const [requestedProjectId] = useState<number | null>(() => {
    const raw = searchParams.get(QUERY_PARAMS.PROJECT_ID);
    return raw !== null && /^\d+$/.test(raw.trim()) ? Number(raw) : null;
  });
  const appliedQueryProjectRef = useRef(false);

  const setActiveProjectId = (id: number) => {
    setStoredProjectId(id);
    // A manual pick supersedes the link-provided project — drop it from the URL.
    if (searchParams.has(QUERY_PARAMS.PROJECT_ID)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(QUERY_PARAMS.PROJECT_ID);
          return next;
        },
        { replace: true },
      );
    }
  };

  // Active context (which section is focused)
  const [activeContext, setActiveContext] =
    useState<ActiveContext>("reportList");

  // Open report list tabs. Keyed per tenant so switching tenants (or a page
  // reload after a tenant switch) doesn't show another tenant's stored tabs.
  const [openTabs, setOpenTabs] = useLocalStorage<ReportList[]>(
    `${STORAGE_KEYS.OPEN_REPORT_LIST_TABS}_${tenantId}`,
    [],
  );
  const [activeTabIndex, setActiveTabIndex] = useLocalStorage(
    `${STORAGE_KEYS.ACTIVE_TAB_INDEX}_${tenantId}`,
    0,
  );

  // Report library state
  const [reportLibraryExpanded, setReportLibraryExpanded] = useLocalStorage(
    STORAGE_KEYS.SHOW_REPORTS_LIBRARY,
    true,
  );
  const [expandedFoldersByTypeStored, setExpandedFoldersByType] = useLocalStorage<
    string[] | Partial<Record<ReportType, string[]>>
  >(STORAGE_KEYS.EXPANDED_FOLDERS, { project: [], administrative: [], custom: [] });
  const expandedFoldersByType = normalizeExpandedFoldersByType(
    expandedFoldersByTypeStored,
  );
  const [selectedReportNode, setSelectedReportNode] =
    useState<ReportTreeItemData | null>(null);

  // Details/parameter panel collapsed state
  const [detailsPanelExpanded, setDetailsPanelExpanded] = useLocalStorage(
    STORAGE_KEYS.SHOW_DETAILS_PANEL,
    true,
  );

  // Selected items in active report list (checkbox state)
  const [selectedListItems, setSelectedListItems] = useState<ReportListItem[]>(
    [],
  );

  // The single focused item whose parameters are shown in the panel.
  // Independent of checkbox selection.
  const [focusedListItem, setFocusedListItem] = useState<ReportListItem | null>(
    null,
  );
  // Ref tracks focused item key for use in SignalR handlers without adding deps
  const focusedItemKeyRef = useRef<string | null>(null);
  focusedItemKeyRef.current = focusedListItem?.reportListItemKey || null;

  // Ref for allReportsResponse used in SignalR handlers to resolve report paths
  const allReportsRef = useRef<{ reportKey: string; path: string }[]>([]);

  // Ref tracks open tab keys for SignalR handlers to suppress toasts for non-open lists
  const openTabKeysRef = useRef<Set<string>>(new Set());
  openTabKeysRef.current = new Set(openTabs.map((t) => t.reportListKey));

  // Snapshot of the current tabs/active index, read by handleTenantChanged so
  // switching back to a previously-visited tenant (this session) restores its
  // tabs instead of always clearing them.
  const openTabsSnapshotRef = useRef<{ tabs: ReportList[]; activeIndex: number }>({
    tabs: [],
    activeIndex: 0,
  });
  openTabsSnapshotRef.current = { tabs: openTabs, activeIndex: activeTabIndex };
  const tabsByTenantRef = useRef<Map<number, { tabs: ReportList[]; activeIndex: number }>>(
    new Map(),
  );

  // Active report parameters state (for parameter panel)
  const [activeReportParameters, setActiveReportParameters] = useState<
    Parameter[]
  >([]);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [openDialogOpen, setOpenDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);

  // Application Insights tracking
  const trackEvent = useAppInsightsEvent();

  // React Query client for cache invalidation
  const queryClient = useQueryClient();

  // State updater that replaces a single item by key (avoids deep arrow nesting)
  const replaceSelectedItem = (key: string, replacement: ReportListItem) =>
    (prev: ReportListItem[]) => prev.map((si) =>
      si.reportListItemKey === key ? replacement : si,
    );

  /**
   * Hydrate and apply a focused item update received from SignalR.
   * Extracted to avoid excessive nesting in the event handler.
   */
  const applyFocusedItemUpdate = useCallback(async (freshItem: ReportListItem) => {
    const reportPath = freshItem.report?.path || allReportsRef.current.find(
      (r) => r.reportKey === freshItem.reportKey,
    )?.path;

    if (!reportPath || !freshItem.reportListItemParameters) {
      setActiveReportParameters([]);
      setFocusedListItem(freshItem);
      setSelectedListItems(replaceSelectedItem(freshItem.reportListItemKey, freshItem));
      return;
    }

    try {
      const paramValues = buildParameterDictFromXml(freshItem.reportListItemParameters);
      const parameters = await reportApi.getReportParameters(reportPath, paramValues);
      const hydratedItem: ReportListItem = {
        ...freshItem,
        report: freshItem.report
          ? { ...freshItem.report, parameters }
          : { reportKey: freshItem.reportKey, name: freshItem.displayName, path: reportPath, tenantId: freshItem.tenantId, parameters } as Report,
      };
      setActiveReportParameters([]);
      setFocusedListItem(hydratedItem);
      setSelectedListItems(replaceSelectedItem(freshItem.reportListItemKey, hydratedItem));
    } catch (error) {
      console.error("Failed to hydrate focused item from SignalR:", error);
      setActiveReportParameters([]);
      setFocusedListItem(freshItem);
      setSelectedListItems(replaceSelectedItem(freshItem.reportListItemKey, freshItem));
    }
  }, []);

  // SignalR real-time updates
  const signalrEventHandlers = useMemo(() => ({
    ReportListCreated: async (data: ReportListEventPayload) => {
      // Invalidate this tenant's report lists to show the new list
      const tenantId = getCachedTenantId(queryClient);
      await queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.list(tenantId) });
      // Only show toast if the user has this list open
      if (openTabKeysRef.current.has(data.reportListKey)) {
        toast.info(t("toast.info.reportListCreated", { key: data.reportListKey }));
      }
    },
    ReportListUpdated: async () => {
      // Invalidate to refresh list metadata
      const tenantId = getCachedTenantId(queryClient);
      await queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.list(tenantId) });
    },
    ReportListDeleted: async (data: ReportListEventPayload) => {
      const tenantId = getCachedTenantId(queryClient);
      // Look up the list name before invalidating cache
      const reportLists = queryClient.getQueryData<ReportList[]>(
        queryKeys.reportLists.list(tenantId),
      );
      const listName = reportLists?.find(
        (list) => list.reportListKey === data.reportListKey,
      )?.name;
      // Remove deleted list from open tabs
      const wasOpen = openTabKeysRef.current.has(data.reportListKey);
      const isNotDeleted = (tab: ReportList) => tab.reportListKey !== data.reportListKey;
      setOpenTabs((tabs) => tabs.filter(isNotDeleted));
      // Clear focused item so the parameter panel doesn't show stale data
      if (wasOpen) {
        setFocusedListItem(null);
        setSelectedListItems((prev) =>
          prev.filter((item) => item.reportListKey !== data.reportListKey),
        );
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.list(tenantId) });
      // Only show toast if the user had this list open
      if (wasOpen) {
        toast.info(t("toast.info.reportListDeleted", { name: listName || data.reportListKey }));
      }
    },
    ReportListItemCreated: async (data: ReportListItemEventPayload) => {
      const tenantId = getCachedTenantId(queryClient);
      // Refresh items for the affected list and wait for fresh data
      await queryClient.refetchQueries({
        queryKey: queryKeys.reportListItems.byList(data.reportListKey),
      });
      // Also refresh report lists so the Open dialog preview reflects the new item
      await queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.list(tenantId) });
      // Look up the new item and list name from cache
      const items = queryClient.getQueryData<ReportListItem[]>(
        queryKeys.reportListItems.byList(data.reportListKey),
      );
      const newItem = items?.find(
        (item) => item.reportListItemKey === data.reportListItemKey,
      );
      const reportLists = queryClient.getQueryData<ReportList[]>(
        queryKeys.reportLists.list(tenantId),
      );
      const listName = reportLists?.find(
        (list) => list.reportListKey === data.reportListKey,
      )?.name;
      if (newItem && openTabKeysRef.current.has(data.reportListKey)) {
        toast.info(t("toast.info.reportListItemCreated", { name: newItem.displayName, listName: listName || "" }));
      }
    },
    ReportListItemUpdated: async (data: ReportListItemEventPayload) => {
      const tenantId = getCachedTenantId(queryClient);
      console.log("🔔 SignalR ReportListItemUpdated received:", {
        reportListItemKey: data.reportListItemKey,
        reportListKey: data.reportListKey,
        focusedItemKey: focusedItemKeyRef.current,
        isFocusedItem: focusedItemKeyRef.current === data.reportListItemKey,
      });

      // Refetch items for the affected list and wait for fresh data
      await queryClient.refetchQueries({
        queryKey: queryKeys.reportListItems.byList(data.reportListKey),
      });
      // Also refresh report lists so the Open dialog preview reflects the update
      await queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.list(tenantId) });

      // Show toast with the report item name
      const freshItems = queryClient.getQueryData<ReportListItem[]>(
        queryKeys.reportListItems.byList(data.reportListKey),
      );
      const updatedItem = freshItems?.find(
        (item) => item.reportListItemKey === data.reportListItemKey,
      );
      if (updatedItem && openTabKeysRef.current.has(data.reportListKey)) {
        // Look up the report list name from cache
        const reportLists = queryClient.getQueryData<ReportList[]>(
          queryKeys.reportLists.list(tenantId),
        );
        const listName = reportLists?.find(
          (list) => list.reportListKey === data.reportListKey,
        )?.name;
        toast.info(t("toast.info.reportListItemUpdated", { name: updatedItem.displayName, listName: listName || "" }));
      }

      // If this is the currently focused item, update it from fresh cache
      // so the parameter panel reflects the other user's changes
      if (focusedItemKeyRef.current === data.reportListItemKey && updatedItem) {
        console.log("🔔 SignalR updating focused item:", {
          foundFreshItem: true,
          freshItemKey: updatedItem.reportListItemKey,
        });
        await applyFocusedItemUpdate(updatedItem);
      }
    },
    ReportListItemDeleted: async (data: ReportListItemEventPayload) => {
      const tenantId = getCachedTenantId(queryClient);
      // Look up item and list names before invalidating
      const items = queryClient.getQueryData<ReportListItem[]>(
        queryKeys.reportListItems.byList(data.reportListKey),
      );
      const deletedItem = items?.find(
        (item) => item.reportListItemKey === data.reportListItemKey,
      );
      const reportLists = queryClient.getQueryData<ReportList[]>(
        queryKeys.reportLists.list(tenantId),
      );
      const listName = reportLists?.find(
        (list) => list.reportListKey === data.reportListKey,
      )?.name;
      // Refresh items for the affected list
      await queryClient.invalidateQueries({
        queryKey: queryKeys.reportListItems.byList(data.reportListKey),
      });
      // Also refresh report lists so the Open dialog preview reflects the deletion
      await queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.list(tenantId) });
      if (deletedItem && openTabKeysRef.current.has(data.reportListKey)) {
        toast.info(t("toast.info.reportListItemDeleted", { name: deletedItem.displayName, listName: listName || "" }));
      }
    },
    MultipleReportListItemsCreated: async (data: MultipleReportListItemsEventPayload) => {
      const tenantId = getCachedTenantId(queryClient);
      // Refresh items for the affected list
      await queryClient.invalidateQueries({
        queryKey: queryKeys.reportListItems.byList(data.reportListKey),
      });
      // Also refresh report lists so the Open dialog preview reflects the new items
      await queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.list(tenantId) });
      const reportLists = queryClient.getQueryData<ReportList[]>(
        queryKeys.reportLists.list(tenantId),
      );
      const listName = reportLists?.find(
        (list) => list.reportListKey === data.reportListKey,
      )?.name;
      if (openTabKeysRef.current.has(data.reportListKey)) {
        toast.info(
          t("toast.info.multipleItemsCreated", {
            count: data.count,
            listName: listName || "",
          }),
        );
      }
    },
    MultipleReportListItemsDeleted: async (data: MultipleReportListItemsEventPayload) => {
      const tenantId = getCachedTenantId(queryClient);
      // Refresh items for the affected list
      await queryClient.invalidateQueries({
        queryKey: queryKeys.reportListItems.byList(data.reportListKey),
      });
      // Also refresh report lists so the Open dialog preview reflects the deletions
      await queryClient.invalidateQueries({ queryKey: queryKeys.reportLists.list(tenantId) });
      const reportLists = queryClient.getQueryData<ReportList[]>(
        queryKeys.reportLists.list(tenantId),
      );
      const listName = reportLists?.find(
        (list) => list.reportListKey === data.reportListKey,
      )?.name;
      if (openTabKeysRef.current.has(data.reportListKey)) {
        toast.info(
          t("toast.info.multipleItemsDeleted", {
            count: data.count,
            listName: listName || "",
          }),
        );
      }
    },
  }), [queryClient, t, setOpenTabs, applyFocusedItemUpdate]);

  const { isConnected: signalrConnected, reconnect: reconnectSignalR } =
    useReportListSignalR({
      eventHandlers: signalrEventHandlers,
    });

  const handleTenantChanged = useCallback((newTenantId: number, previousTenantId: number) => {
    // Snapshot this tenant's tabs before switching away, and restore the new
    // tenant's tabs if we've already visited it earlier this session.
    tabsByTenantRef.current.set(previousTenantId, openTabsSnapshotRef.current);
    const restored = tabsByTenantRef.current.get(newTenantId);
    setOpenTabs(restored?.tabs ?? []);
    setActiveTabIndex(restored?.activeIndex ?? 0);
    setFocusedListItem(null);
    setSelectedListItems([]);
    setActiveReportParameters([]);
    // Close any report-list dialogs so stale previous-tenant selections aren't acted on.
    setCreateDialogOpen(false);
    setOpenDialogOpen(false);
    setDuplicateDialogOpen(false);
    setRenameDialogOpen(false);
    setDeleteDialogOpen(false);
    setDeliveryDialogOpen(false);
    toast.info(t("toast.info.tenantChanged"));
  }, [
    setOpenTabs,
    setActiveTabIndex,
    setFocusedListItem,
    setSelectedListItems,
    setActiveReportParameters,
    setCreateDialogOpen,
    setOpenDialogOpen,
    setDuplicateDialogOpen,
    setRenameDialogOpen,
    setDeleteDialogOpen,
    setDeliveryDialogOpen,
    t,
  ]);

  useTenantChangeSync({
    reconnect: reconnectSignalR,
    onTenantChanged: handleTenantChanged,
  });

  // Data queries
  const { data: projects = [] } = useProjects();
  // All available reports for the report library and parameter lookups
  const { data: allReportsResponse = [], isLoading: reportsLoading } =
    useReports();
  allReportsRef.current = allReportsResponse;
  const { data: availableReportLists = [] } = useReportLists();

  // Auto-select first project if none selected or stored project no longer exists.
  // A `projectId` query param present on first load is applied here instead, so
  // it doesn't race with the default-selection logic below.
  useEffect(() => {
    if (projects.length === 0) return;

    if (!appliedQueryProjectRef.current) {
      appliedQueryProjectRef.current = true;
      if (requestedProjectId !== null) {
        const matched = projects.find(
          (p) => p.projectId === requestedProjectId,
        );
        if (matched) {
          setStoredProjectId(matched.projectId);
          return;
        }
        toast.warning(
          t("toast.warning.projectNotFound", { id: requestedProjectId }),
        );
      }
    }

    const projectExists = projects.some(
      (p) => p.projectId === activeProjectId,
    );
    if (!activeProjectId || !projectExists) {
      setStoredProjectId(projects[0].projectId);
    }
  }, [projects, activeProjectId, setStoredProjectId, requestedProjectId, t]);

  // Mutations
  const createReportListMutation = useCreateReportList();
  const updateReportListMutation = useUpdateReportList();
  const deleteReportListMutation = useDeleteReportList();
  const createMultipleItemsMutation = useCreateMultipleReportListItems();
  const updateReportListItemMutation = useUpdateReportListItem();
  const deleteReportListItemMutation = useDeleteReportListItem();
  const deleteMultipleReportListItemsMutation =
    useDeleteMultipleReportListItems();
  const generatePDFMutation = useGeneratePDF();

  // Build report trees, one per report type/tab
  const reportTreesByType = buildReportTreesByType(allReportsResponse);

  // Get active report list and its items (shares React Query cache with section component)
  const activeReportList = openTabs[activeTabIndex] || null;
  const { data: activeListItems = [] } = useReportListItems(
    activeReportList?.reportListKey || "",
  );

  // Filter selections to only items belonging to the active tab so that
  // actions (preview, save PDF, delete) never operate on inactive-tab items.
  const activeTabSelectedItems = filterItemsByReportList(
    selectedListItems,
    activeReportList?.reportListKey,
  );

  const handleActiveTabSelectionChange = (items: ReportListItem[]) => {
    setActiveContext("reportList");

    if (!activeReportList?.reportListKey) {
      setSelectedListItems([]);
      return;
    }

    // Replace only the active tab's portion of the global selection list.
    // `items` comes from the grid's onSelectionChange callback, which only
    // ever emits items belonging to the active tab. Selections in background
    // tabs are preserved by keeping entries whose reportListKey differs from
    // the active tab.
    setSelectedListItems((previousItems) => [
      ...previousItems.filter(
        (item) => item.reportListKey !== activeReportList.reportListKey,
      ),
      ...items,
    ]);
  };

  const handleActiveItemChange = (item: ReportListItem | null) => {
    setActiveContext("reportList");
    setFocusedListItem(item);
    setSelectedReportNode(null);
  };

  const singleSelectedListItem = useMemo(
    () =>
      activeContext === "reportList" && focusedListItem
        ? focusedListItem
        : null,
    [activeContext, focusedListItem],
  );

  const hasMultipleListSelection =
    activeContext === "reportList" &&
    !focusedListItem &&
    activeTabSelectedItems.length > 1;

  // Determine which report to show parameters for
  const activeReportPath = useMemo(() => {
    if (
      activeContext === "reportLibrary" &&
      selectedReportNode?.type === "file"
    ) {
      return selectedReportNode.path!;
    }
    if (activeContext === "reportList" && singleSelectedListItem) {
      // First try the report object if populated
      if (singleSelectedListItem.report?.path) {
        return singleSelectedListItem.report.path;
      }
      // Otherwise look up the path from reportResponses using reportKey
      const reportResponse = allReportsResponse.find(
        (r) => r.reportKey === singleSelectedListItem.reportKey,
      );
      return reportResponse?.path || null;
    }
    return null;
  }, [
    activeContext,
    selectedReportNode,
    singleSelectedListItem,
    allReportsResponse,
  ]);

  // Build context parameters dict to pass to API for proper cascading
  // This ensures parameters like BatchGroup and ComponentIDs get correct values
  // based on the active project context
  const contextParametersDict = useMemo(() => {
    const context = {
      ProjectId: activeProjectId.toString(),
    };


    return context;
  }, [activeProjectId]);

  // Query parameters for the active report WITH context for proper cascading
  // Passing context ensures cascading parameters (BatchGroup, ComponentIDs, etc.)
  // receive correct values based on ProjectId from the start
  const { data: fetchedParameters } = useReportParameters(
    activeReportPath ?? "",
    contextParametersDict,
  );

  // Helper function to create a default Int parameter
  const createIntParameter = useCallback(
    (name: string, value: number): IntParameter => ({
      name,
      type: "Int",
      text: name,
      multiValue: false,
      allowNull: false,
      isVisible: false,
      autoRefresh: false,
      hasChildParameters: false,
      childParameters: [],
      availableValues: [],
      children: [],
      tenantId: 0,
      intValue: value,
    }),
    [],
  );

  // Helper function to create a default String parameter
  const createStringParameter = useCallback(
    (name: string, value: string): StringParameter => ({
      name,
      type: "String",
      text: name,
      multiValue: false,
      allowNull: false,
      isVisible: false,
      autoRefresh: false,
      hasChildParameters: false,
      childParameters: [],
      availableValues: [],
      children: [],
      tenantId: 0,
      stringValue: value,
    }),
    [],
  );

  // Helper function to update or add a context parameter
  const upsertContextParameter = useCallback(
    (
      params: Parameter[],
      name: string,
      type: "Int" | "String",
      value: number | string,
    ): Parameter[] => {
      const result = [...params];
      const existingIndex = result.findIndex((p) => p.name === name);

      if (existingIndex >= 0) {
        // Update existing parameter
        const existing = result[existingIndex];
        result[existingIndex] =
          type === "Int"
            ? ({ ...existing, intValue: value } as IntParameter)
            : ({ ...existing, stringValue: value } as StringParameter);
      } else {
        // Add new parameter
        result.push(
          type === "Int"
            ? createIntParameter(name, value as number)
            : createStringParameter(name, value as string),
        );
      }

      return result;
    },
    [createIntParameter, createStringParameter],
  );

  // Helper function to check if a value is available in parameter options
  const isValueAvailable = useCallback((param: Parameter, value: string): boolean => {
    return param.availableValues.some((av) => String(av.value) === value);
  }, []);

  // Helper function to update project-related parameters
  const updateProjectParameter = useCallback(
    (
      param: Parameter,
      activeProjectId: number,
      selectedProject: { projectId: number; projectNumber: string } | undefined,
    ): Parameter | null => {
      if (param.type === "Int") {
        return { ...param, intValue: activeProjectId };
      }

      if (!selectedProject) return null;

      const valueToSet = String(selectedProject.projectId);

      if (param.type === "String") {
        if (isValueAvailable(param, valueToSet)) {
          return { ...param, stringValue: valueToSet };
        }
        console.warn(
          `Selected project ${selectedProject.projectNumber} (ID: ${valueToSet}) ` +
            `is not available in parameter "${param.name}". ` +
            `Available options: ${param.availableValues.map((av) => av.name).join(", ")}`,
        );
        return null;
      }

      if (param.type === "MultiSelect") {
        if (isValueAvailable(param, valueToSet)) {
          return { ...param, stringValues: [valueToSet] };
        }
        console.warn(
          `Selected project ${selectedProject.projectNumber} (ID: ${valueToSet}) ` +
            `is not available in parameter "${param.name}".`,
        );
        return null;
      }

      return null;
    },
    [isValueAvailable],
  );

  // Helper function to inject context parameters into the parameter array
  const applyContextParameters = useCallback(
    (params: Parameter[]): Parameter[] => {
      if (params.length === 0) return params;

      let result = [...params];

      // Look up the selected project
      const selectedProject = projects.find(
        (p) => p.projectId === activeProjectId,
      );

      // Define context parameters to inject/update
      const contextParams = [
        { name: "ProjectId", type: "Int" as const, value: activeProjectId },
      ];

      // Inject or update each context parameter
      for (const { name, type, value } of contextParams) {
        result = upsertContextParameter(result, name, type, value);
      }

      // Update any project-related parameters
      const projectRelatedNames = new Set(["ProjectId", "Project", "ProjectNumber", "ProjectNo"]);

      result = result.map((param) => {
        if (!projectRelatedNames.has(param.name)) return param;

        const updated = updateProjectParameter(param, activeProjectId, selectedProject);
        return updated ?? param;
      });

      return result;
    },
    [
      activeProjectId,
      projects,
      upsertContextParameter,
      updateProjectParameter,
    ],
  );

  // Sync parameter data to local editable state.
  // Report Library uses API-fetched parameters.
  // Report List prefers hydrated item parameters (from stored XML values),
  // then falls back to fetched defaults when hydration is not yet available.
  useEffect(() => {
    if (!activeReportPath) {
      // Only clear if there are actually parameters to clear
      setActiveReportParameters((prev) => (prev.length > 0 ? [] : prev));
      return;
    }

    let baseParams: Parameter[] = [];

    if (activeContext === "reportList") {
      if (singleSelectedListItem?.report?.parameters) {
        baseParams = singleSelectedListItem.report.parameters;
      } else if (fetchedParameters) {
        baseParams = fetchedParameters;
      }
    } else if (fetchedParameters) {
      baseParams = fetchedParameters;
    }

    // Apply context parameters (ProjectId)
    const contextParams = applyContextParameters(baseParams);
    const paramsWithContext =
      activeContext === "reportList" && singleSelectedListItem
        ? clearUnavailableParameterSelections(
            singleSelectedListItem.reportListItemParameters,
            contextParams,
          )
        : contextParams;

    // Use functional update to check if update is needed
    setActiveReportParameters((prev) => {
      // Only update if parameters have actually changed (compare structure and values)
      const haveSameParameters =
        prev.length === paramsWithContext.length &&
        prev.length > 0 && // Don't consider empty arrays as "same"
        paramsWithContext.every((newParam, idx) => {
          const oldParam = prev[idx];
          if (
            oldParam?.name !== newParam.name ||
            oldParam.type !== newParam.type
          ) {
            return false;
          }

          // Compare values based on parameter type
          switch (newParam.type) {
            case "Int":
              return (oldParam as IntParameter).intValue === newParam.intValue;
            case "String":
              return (oldParam as StringParameter).stringValue === newParam.stringValue;
            case "Bool":
              return (oldParam as BoolParameter).boolValue === newParam.boolValue;
            case "DateTime":
              return (oldParam as DateTimeParameter).dateTimeValue === newParam.dateTimeValue;
            case "MultiSelect": {
              const oldValues = (oldParam as MultiSelectParameter).stringValues;
              const newValues = newParam.stringValues;
              return (
                oldValues.length === newValues.length &&
                oldValues.every((v: string, i: number) => v === newValues[i])
              );
            }
            default:
              return false;
          }
        });

      if (haveSameParameters) {
        return prev;
      }
      return paramsWithContext;
    });
  }, [
    activeContext,
    activeReportPath,
    fetchedParameters,
    singleSelectedListItem,
    activeProjectId,
    applyContextParameters,
  ]);

  // Build the active report object for the parameter panel
  const libraryActiveReport: Report | null = useMemo(
    () =>
      activeContext === "reportLibrary" && selectedReportNode?.type === "file"
        ? {
            reportKey: selectedReportNode.reportKey!,
            name: selectedReportNode.name,
            path: selectedReportNode.path!,
            tenantId: 0,
            description: selectedReportNode.description,
            reportVersion: selectedReportNode.reportVersion,
            parameters: activeReportParameters,
          }
        : null,
    [activeContext, selectedReportNode, activeReportParameters],
  );

  const listActiveReport: Report | null = useMemo(
    () =>
      activeContext === "reportList" && singleSelectedListItem
        ? ({
            ...(singleSelectedListItem.report || {
              reportKey: singleSelectedListItem.reportKey,
              name: singleSelectedListItem.displayName,
              path:
                allReportsResponse.find(
                  (r) => r.reportKey === singleSelectedListItem.reportKey,
                )?.path || "",
              tenantId: singleSelectedListItem.tenantId,
            }),
            parameters: activeReportParameters,
          } as Report)
        : null,
    [
      activeContext,
      singleSelectedListItem,
      allReportsResponse,
      activeReportParameters,
    ],
  );

  const activeReport: Report | null = libraryActiveReport ?? listActiveReport;

  // Build thumbnail node for preview image (works for both library and list contexts)
  const thumbnailNode: ReportTreeItemData | null = useMemo(() => {
    if (
      activeContext === "reportLibrary" &&
      selectedReportNode?.type === "file"
    ) {
      return selectedReportNode;
    }

    if (activeContext === "reportList" && singleSelectedListItem) {
      // Construct a node from the list item for thumbnail display
      const reportResponse = allReportsResponse.find(
        (r) => r.reportKey === singleSelectedListItem.reportKey,
      );

      if (reportResponse) {
        return {
          type: "file" as const,
          id: `list-item-${singleSelectedListItem.reportListItemKey}`,
          name: singleSelectedListItem.displayName,
          reportName: reportResponse.name,
          reportKey: singleSelectedListItem.reportKey,
          path: reportResponse.path,
          description: reportResponse.description,
          reportVersion: reportResponse.reportVersion,
        };
      }
    }

    return null;
  }, [
    activeContext,
    selectedReportNode,
    singleSelectedListItem,
    allReportsResponse,
  ]);

  // Handlers
  const handlePreview = (
    itemsToPreview?: Array<{
      reportKey: string;
      reportListItemParameters?: string;
    }>,
  ) => {
    const items = itemsToPreview ?? activeTabSelectedItems;
    if (items.length === 0) return;

    // Build all preview URLs first
    const previewUrls: string[] = [];
    for (const item of items) {
      const report = allReportsResponse.find(
        (r) => r.reportKey === item.reportKey,
      );
      if (!report) {
        console.warn(`Report not found for key: ${item.reportKey}`);
        continue;
      }

      const parametersJson = item.reportListItemParameters
        ? (() => {
            const entries = deserializeParameters(
              item.reportListItemParameters,
            );
            const dict: Record<string, string> = {};

            for (const entry of entries) {
              if (EXCLUDED_PDF_PARAMS.has(entry.parameterName)) {
                continue;
              }
            dict[entry.parameterName] = entry.selectedValues.join(",");
          }

          dict["ProjectId"] = activeProjectId.toString();

          return JSON.stringify(dict);
          })()
        : JSON.stringify({
            ProjectId: activeProjectId.toString(),
          });

      const params = new URLSearchParams();
      params.append("ReportKey", item.reportKey);
      params.append("ReportPath", report.path);
      params.append("Parameters", parametersJson);
      params.append("Source", "report_list");

      previewUrls.push(`/ReportPreview?${params.toString()}`);
    }

    // Open all windows synchronously to preserve user gesture context
    const openedWindows = previewUrls.map((url) => window.open(url, "_blank"));

    // Notify user if any were blocked by popup blocker
    const blockedCount = openedWindows.filter((w) => !w).length;
    if (blockedCount > 0) {
      toast.warning(
        t("toast.warning.popupBlocked", { count: blockedCount }),
        { duration: Infinity, closeButton: true },
      );
    }
  };

  const downloadPdfs = async (
    items: Array<{
      displayName: string;
      reportKey: string;
      reportListItemParameters?: string;
      report?: Report | null;
    }>,
    source: "report_list" | "save_all" | "library" = "report_list",
  ) => {
    if (items.length === 0) return;

    track(source === "save_all" ? "pdf_bulk_saved" : "pdf_saved", {
      item_count: items.length,
      source,
    });

    try {
      const generatePdf = async (item: (typeof items)[0]) => {
        // Get report path - use report path if available, fall back to reportKey
        const reportPath =
          item.report?.path ||
          allReportsResponse.find((r) => r.reportKey === item.reportKey)
            ?.path ||
          item.reportKey;

        // Build parameters for this report
        const parameters: Record<string, string> = {
          ProjectId: activeProjectId.toString(),
        };

        // Parse report-specific parameters from XML if available
        let hasStoredParams = false;
        if (item.reportListItemParameters) {
          const paramEntries = deserializeParameters(
            item.reportListItemParameters,
          );
          if (paramEntries.length > 0) {
            hasStoredParams = true;
            for (const entry of paramEntries) {
              // Join multiple values with comma for multi-select parameters
              parameters[entry.parameterName] = entry.selectedValues.join(",");
            }
          }
        }

        // If no stored parameters, fetch default parameters from API
        // Keep track of initial parameters that should not be overwritten
        const preservedParams = new Set([
          "ProjectId",
        ]);

        if (!hasStoredParams) {
          if (item.report?.parameters) {
            // Use report.parameters if available (only visible params, don't overwrite preserved)
            item.report.parameters
              .filter(
                (param) => param.isVisible && !preservedParams.has(param.name),
              )
              .forEach((param) => {
                parameters[param.name] = getParameterStringValue(param);
              });
          } else {
            // Fetch default parameters from API
            try {
              const defaultParams = await reportApi.getReportParameters(
                reportPath,
                parameters,
              );
              // Only include visible parameters, don't overwrite preserved params
              defaultParams
                .filter(
                  (param) =>
                    param.isVisible && !preservedParams.has(param.name),
                )
                .forEach((param) => {
                  parameters[param.name] = getParameterStringValue(param);
                });
            } catch (error) {
              console.warn(
                `Failed to fetch parameters for ${item.displayName}:`,
                error,
              );
              // Continue without report-specific parameters
            }
          }
        }

        return generatePDFMutation.mutateAsync({
          reportClassName: reportPath,
          parameters,
        });
      };

      if (items.length === 1) {
        // Single PDF - download directly
        const toastId = toast.info(t("toast.info.savingPdf", { name: items[0].displayName }));
        const blob = await generatePdf(items[0]);
        const timestamp = formatFilenameTimestamp();
        saveAs(blob, `${items[0].displayName}_${timestamp}.pdf`);
        toast.success(t("toast.success.pdfSaved"), { id: toastId });
      } else {
        // Multiple PDFs - bundle into ZIP (lazy-load jszip)
        const zipToastId = toast.info(t("toast.info.generatingPdfs", { count: items.length }));
        const { default: JSZip } = await import("jszip");
        const zip = new JSZip();
        const pdfToastId = "pdfGenerated";
        await Promise.all(
          items.map(async (item) => {
            const blob = await generatePdf(item);
            const timestamp = formatFilenameTimestamp();
            toast.success(t("toast.success.pdfGenerated", { name: item.displayName }), { id: pdfToastId });
            zip.file(`${item.displayName}_${timestamp}.pdf`, blob);
          }),
        );

        toast.info(t("toast.info.creatingZip"), { id: zipToastId });
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const listName = activeReportList?.name || "reports";
        saveAs(zipBlob, `${listName}.zip`);
        toast.success(t("toast.success.pdfsSavedAsZip", { count: items.length }), { id: zipToastId });
      }
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      toast.error(t("toast.error.pdfFailed"));
    }
  };

  const handleSavePdf = (
    itemsToSave?: Array<{
      displayName: string;
      reportKey: string;
      reportListItemParameters?: string;
      report?: Report | null;
    }>,
  ) => downloadPdfs(itemsToSave ?? activeTabSelectedItems);

  const handleSaveAllPdf = () => downloadPdfs(activeListItems, "save_all");

  const handleCreateReportList = async (name: string) => {
    try {
      const userKey = getUserKey();
      const result = await createReportListMutation.mutateAsync({
        reportList: {
          name,
          createdUserKey: userKey,
          tenantId,
        },
        reportDelivery: createDefaultDeliveryDto({
          createdUserKey: userKey,
          tenantId,
        }),
      });

      // Build a minimal ReportList from the known data and add to open tabs.
      // We can't search availableReportLists here because the query invalidation
      // refetch hasn't completed yet (race condition). The background refetch will
      // update the full list data, but we need the tab open immediately.
      const now = new Date().toISOString();
      const newList: ReportList = {
        reportListKey: result.reportListKey,
        name,
        tenantId,
        reportListItems: [],
        createdUserKey: userKey,
        lastModifiedUserKey: userKey,
        createdDateTime: now,
        lastModifiedDateTime: now,
      };
      if (!openTabs.some((t) => t.reportListKey === newList.reportListKey)) {
        const updated = [...openTabs, newList];
        setOpenTabs(updated);
        setActiveTabIndex(updated.length - 1);
      }

      toast.success(t("toast.success.created", { name }));
      track("report_list_created");
      setCreateDialogOpen(false);
    } catch (error) {
      console.error("Failed to create report list:", error);
      toast.error(t("toast.error.createFailed"));
    }
  };

  const handleOpenReportLists = (lists: ReportList[]) => {
    track("report_list_opened", { list_count: lists.length });
    const updated = [...openTabs];
    let firstNewIndex = -1;
    for (const list of lists) {
      const existingIndex = updated.findIndex(
        (t) => t.reportListKey === list.reportListKey,
      );
      if (existingIndex === -1) {
        updated.push(list);
        if (firstNewIndex === -1) firstNewIndex = updated.length - 1;
      } else if (firstNewIndex === -1) {
        // List was already open — switch to it
        firstNewIndex = existingIndex;
      }
    }
    setOpenTabs(updated);
    if (firstNewIndex !== -1) {
      setActiveTabIndex(firstNewIndex);
    }
    // Clear focused item so the parameter panel resets for the new tab
    setFocusedListItem(null);
  };

  const handleDuplicateReportList = async (newName: string) => {
    if (!activeReportList) return;

    try {
      const userKey = getUserKey();
      // Create the new report list
      const result = await createReportListMutation.mutateAsync({
        reportList: {
          name: newName,
          createdUserKey: userKey,
          tenantId,
        },
        reportDelivery: createDefaultDeliveryDto({
          reportDeliveryTypeKey: resolveGuid(
            activeReportList.defaultReportDelivery?.reportDeliveryTypeKey,
            DEFAULT_DELIVERY_TYPE_KEY,
          ),
          reportFileTypeKey: resolveGuid(
            activeReportList.defaultReportDelivery?.reportFileTypeKey,
            DEFAULT_FILE_TYPE_KEY,
          ),
          reportCollationTypeKey: resolveGuid(
            activeReportList.defaultReportDelivery?.reportCollationTypeKey,
            DEFAULT_COLLATION_TYPE_KEY,
          ),
          createdUserKey: userKey,
          tenantId,
        }),
      });

      // Copy all items from the source list
      if (activeReportList.reportListItems.length > 0) {
        const itemRequests = activeReportList.reportListItems.map(
          (item, index) => ({
            reportListItem: {
              reportListKey: result.reportListKey,
              reportKey: item.reportKey,
              displayName: item.displayName,
              displayOrder: index,
              reportListItemParameters: sanitizeParameterXml(
                item.reportListItemParameters,
              ),
              createdUserKey: userKey,
              tenantId,
            },
            reportDelivery: createDefaultDeliveryDto({
              reportDeliveryTypeKey: item.reportDelivery.reportDeliveryTypeKey,
              reportFileTypeKey: item.reportDelivery.reportFileTypeKey,
              reportCollationTypeKey:
                item.reportDelivery.reportCollationTypeKey,
              createdUserKey: userKey,
              tenantId,
            }),
          }),
        );

        await createMultipleItemsMutation.mutateAsync(itemRequests);
      }

      // Build a minimal ReportList from the known data (same pattern as create).
      // We can't rely on availableReportLists here because the query invalidation
      // refetch hasn't completed yet.
      const now = new Date().toISOString();
      const newList: ReportList = {
        reportListKey: result.reportListKey,
        name: newName,
        tenantId,
        reportListItems: [],
        createdUserKey: userKey,
        lastModifiedUserKey: userKey,
        createdDateTime: now,
        lastModifiedDateTime: now,
      };
      if (!openTabs.some((t) => t.reportListKey === newList.reportListKey)) {
        const updated = [...openTabs, newList];
        setOpenTabs(updated);
        setActiveTabIndex(updated.length - 1);
      }

      toast.success(
        t("toast.success.duplicated", { source: activeReportList.name, target: newName }),
      );
      setDuplicateDialogOpen(false);
    } catch (error) {
      console.error("Failed to duplicate report list:", error);
      toast.error(t("toast.error.duplicateFailed"));
    }
  };

  const handleRenameReportList = async (newName: string) => {
    if (!activeReportList) return;

    try {
      const userKey = getUserKey();
      await updateReportListMutation.mutateAsync({
        reportList: {
          reportListKey: activeReportList.reportListKey,
          name: newName,
          lastModifiedUserKey: userKey,
          defaultReportDeliveryKey:
            activeReportList.defaultReportDeliveryKey || EMPTY_GUID,
          tenantId,
        },
        reportDelivery: createDefaultDeliveryUpdateDto({
          reportDeliveryKey:
            activeReportList.defaultReportDelivery?.reportDeliveryKey ||
            EMPTY_GUID,
          reportDeliveryTypeKey: resolveGuid(
            activeReportList.defaultReportDelivery?.reportDeliveryTypeKey,
            DEFAULT_DELIVERY_TYPE_KEY,
          ),
          reportFileTypeKey: resolveGuid(
            activeReportList.defaultReportDelivery?.reportFileTypeKey,
            DEFAULT_FILE_TYPE_KEY,
          ),
          reportCollationTypeKey: resolveGuid(
            activeReportList.defaultReportDelivery?.reportCollationTypeKey,
            DEFAULT_COLLATION_TYPE_KEY,
          ),
          createdUserKey:
            activeReportList.defaultReportDelivery?.createdUserKey || userKey,
          lastModifiedUserKey: userKey,
          tenantId,
        }),
      });

      // Update openTabs with new name
      const updatedTabs = openTabs.map((tab) =>
        tab.reportListKey === activeReportList.reportListKey
          ? { ...tab, name: newName }
          : tab,
      );
      setOpenTabs(updatedTabs);

      toast.success(t("toast.success.renamed", { name: newName }));
      setRenameDialogOpen(false);
    } catch (error) {
      console.error("Failed to rename report list:", error);
      toast.error(t("toast.error.renameFailed"));
    }
  };

  const handleDeleteReportList = async () => {
    if (!activeReportList) return;

    try {
      await deleteReportListMutation.mutateAsync(
        activeReportList.reportListKey,
      );

      // Remove from openTabs and adjust active tab index if needed
      const keyToRemove = activeReportList.reportListKey;
      setOpenTabs((prev) => {
        const newTabs = prev.filter((t) => t.reportListKey !== keyToRemove);
        if (activeTabIndex >= newTabs.length && newTabs.length > 0) {
          setActiveTabIndex(newTabs.length - 1);
        } else if (newTabs.length === 0) {
          setActiveTabIndex(0);
        }
        return newTabs;
      });
      // Clear focused item and selections so the parameter panel resets
      setFocusedListItem(null);
      setSelectedListItems((prev) =>
        prev.filter((item) => item.reportListKey !== keyToRemove),
      );

      toast.success(t("toast.success.deleted", { name: activeReportList.name }));
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Failed to delete report list:", error);
      toast.error(t("toast.error.deleteFailed"));
    }
  };

  const handleDeliveryConfigSave = async (
    delivery: Partial<ReportDelivery>,
  ) => {
    if (!activeReportList) return;

    try {
      const userKey = getUserKey();
      await updateReportListMutation.mutateAsync({
        reportList: {
          reportListKey: activeReportList.reportListKey,
          name: activeReportList.name,
          lastModifiedUserKey: userKey,
          defaultReportDeliveryKey:
            activeReportList.defaultReportDeliveryKey || EMPTY_GUID,
          tenantId,
        },
        reportDelivery: {
          ...createDefaultDeliveryUpdateDto({
            reportDeliveryKey:
              activeReportList.defaultReportDelivery?.reportDeliveryKey ||
              EMPTY_GUID,
            createdUserKey:
              activeReportList.defaultReportDelivery?.createdUserKey || userKey,
            lastModifiedUserKey: userKey,
            tenantId,
          }),
          ...delivery,
          lastModifiedUserKey: userKey,
        },
      });

      toast.success(t("toast.success.deliverySaved"));
    } catch (error) {
      console.error("Failed to save delivery configuration:", error);
      toast.error(t("toast.error.deliveryFailed"));
    }
  };

  const handleTabRename = async (reportListKey: string, newName: string) => {
    const tab = openTabs.find((t) => t.reportListKey === reportListKey);
    if (!tab) return;

    try {
      const userKey = getUserKey();
      await updateReportListMutation.mutateAsync({
        reportList: {
          reportListKey,
          name: newName,
          lastModifiedUserKey: userKey,
          defaultReportDeliveryKey: tab.defaultReportDeliveryKey || EMPTY_GUID,
          tenantId,
        },
        reportDelivery: createDefaultDeliveryUpdateDto({
          reportDeliveryKey:
            tab.defaultReportDelivery?.reportDeliveryKey || EMPTY_GUID,
          reportDeliveryTypeKey: resolveGuid(
            tab.defaultReportDelivery?.reportDeliveryTypeKey,
            DEFAULT_DELIVERY_TYPE_KEY,
          ),
          reportFileTypeKey: resolveGuid(
            tab.defaultReportDelivery?.reportFileTypeKey,
            DEFAULT_FILE_TYPE_KEY,
          ),
          reportCollationTypeKey: resolveGuid(
            tab.defaultReportDelivery?.reportCollationTypeKey,
            DEFAULT_COLLATION_TYPE_KEY,
          ),
          createdUserKey: tab.defaultReportDelivery?.createdUserKey || userKey,
          lastModifiedUserKey: userKey,
          tenantId,
        }),
      });

      // Update openTabs with new name
      const updatedTabs = openTabs.map((t) =>
        t.reportListKey === reportListKey ? { ...t, name: newName } : t,
      );
      setOpenTabs(updatedTabs);

      toast.success(t("toast.success.renamed", { name: newName }));
    } catch (error) {
      console.error("Failed to rename report list:", error);
      toast.error(t("toast.error.renameFailed"));
    }
  };

  const handleCloseTab = (reportListKey: string) => {
    const updated = openTabs.filter((t) => t.reportListKey !== reportListKey);
    setOpenTabs(updated);
    if (activeTabIndex >= updated.length && updated.length > 0) {
      setActiveTabIndex(updated.length - 1);
    } else if (updated.length === 0) {
      setActiveTabIndex(0);
    }
    // Clear focused item and selections so the parameter panel doesn't show
    // stale data from the closed tab
    setFocusedListItem(null);
    setSelectedListItems((prev) =>
      prev.filter((item) => item.reportListKey !== reportListKey),
    );
  };

  const handleDeleteItemSuccess = (item: ReportListItem) => {
    toast.success(t("toast.success.deleted", { name: item.displayName }));
    setSelectedListItems((prev) =>
      prev.filter((si) => si.reportListItemKey !== item.reportListItemKey),
    );
  };

  const handleItemContextAction = async (
    command: ContextMenuCommand,
    item: ReportListItem,
  ) => {
    switch (command) {
      case "preview":
        setFocusedListItem(item);
        setSelectedReportNode(null);
        handlePreview([item]);
        break;
      case "savePdf":
        setFocusedListItem(item);
        setSelectedReportNode(null);
        await handleSavePdf([item]);
        break;
      case "duplicateItem":
        // Duplicate the item in the same list
        if (activeReportList) {
          const userKey = getUserKey();
          // Use item's delivery settings if available, otherwise fall back to list defaults
          const itemDelivery = item.reportDelivery;
          const defaultDelivery = activeReportList.defaultReportDelivery;
          createMultipleItemsMutation.mutate(
            [
              {
                reportListItem: {
                  reportListKey: activeReportList.reportListKey,
                  reportKey: item.reportKey,
                  displayName: `${item.displayName} (Copy)`,
                  // Append duplicate to end of current list to avoid displayOrder collisions.
                  displayOrder: activeListItems.length,
                  reportListItemParameters: sanitizeParameterXml(
                    item.reportListItemParameters,
                  ),
                  createdUserKey: userKey,
                  tenantId,
                },
                reportDelivery: createDefaultDeliveryDto({
                  reportDeliveryTypeKey: resolveGuid(
                    itemDelivery?.reportDeliveryTypeKey,
                    resolveGuid(
                      defaultDelivery?.reportDeliveryTypeKey,
                      DEFAULT_DELIVERY_TYPE_KEY,
                    ),
                  ),
                  reportFileTypeKey: resolveGuid(
                    itemDelivery?.reportFileTypeKey,
                    resolveGuid(
                      defaultDelivery?.reportFileTypeKey,
                      DEFAULT_FILE_TYPE_KEY,
                    ),
                  ),
                  reportCollationTypeKey: resolveGuid(
                    itemDelivery?.reportCollationTypeKey,
                    resolveGuid(
                      defaultDelivery?.reportCollationTypeKey,
                      DEFAULT_COLLATION_TYPE_KEY,
                    ),
                  ),
                  createdUserKey: userKey,
                  tenantId,
                }),
              },
            ],
            {
              onSuccess: () => toast.success(t("toast.success.duplicatedItem")),
              onError: () => toast.error(t("toast.error.duplicateItemFailed")),
            },
          );
        }
        break;
      case "deleteItem":
        setFocusedListItem(null);
        setSelectedListItems((prev) =>
          prev.filter((si) => si.reportListItemKey !== item.reportListItemKey),
        );
        deleteReportListItemMutation.mutate(
          {
            reportListItemKey: item.reportListItemKey,
            reportListKey: item.reportListKey,
          },
          {
            onSuccess: () => handleDeleteItemSuccess(item),
            onError: () => toast.error(t("toast.error.deleteItemFailed")),
          },
        );
        break;
      case "deleteSelected":
        if (activeTabSelectedItems.length > 0) {
          const activeListKey = openTabs[activeTabIndex]?.reportListKey || "";
          deleteMultipleReportListItemsMutation.mutate(
            {
              keys: activeTabSelectedItems.map((si) => si.reportListItemKey),
              reportListKey: activeListKey,
            },
            {
              onSuccess: () => {
                toast.success(
                  t("toast.success.deletedItems", { count: activeTabSelectedItems.length }),
                );
                setSelectedListItems([]);
              },
              onError: () => toast.error(t("toast.error.deleteItemsFailed")),
            },
          );
        }
        break;
      case "previewAll":
        handlePreview(activeListItems);
        break;
      case "saveAllPdf":
        await handleSaveAllPdf();
        break;
      case "previewSelected":
        handlePreview(activeTabSelectedItems);
        break;
      case "saveSelectedPdf":
        await handleSavePdf(activeTabSelectedItems);
        break;
      case "duplicateSelected":
        if (activeReportList && activeTabSelectedItems.length > 0) {
          const userKey = getUserKey();
          const defaultDelivery = activeReportList.defaultReportDelivery;
          createMultipleItemsMutation.mutate(
            activeTabSelectedItems.map((selectedItem, index) => {
              const itemDelivery = selectedItem.reportDelivery;
              return {
                reportListItem: {
                  reportListKey: activeReportList.reportListKey,
                  reportKey: selectedItem.reportKey,
                  displayName: `${selectedItem.displayName} (Copy)`,
                  displayOrder: activeListItems.length + index,
                  reportListItemParameters: sanitizeParameterXml(
                    selectedItem.reportListItemParameters,
                  ),
                  createdUserKey: userKey,
                  tenantId,
                },
                reportDelivery: createDefaultDeliveryDto({
                  reportDeliveryTypeKey: resolveGuid(
                    itemDelivery?.reportDeliveryTypeKey,
                    resolveGuid(
                      defaultDelivery?.reportDeliveryTypeKey,
                      DEFAULT_DELIVERY_TYPE_KEY,
                    ),
                  ),
                  reportFileTypeKey: resolveGuid(
                    itemDelivery?.reportFileTypeKey,
                    resolveGuid(
                      defaultDelivery?.reportFileTypeKey,
                      DEFAULT_FILE_TYPE_KEY,
                    ),
                  ),
                  reportCollationTypeKey: resolveGuid(
                    itemDelivery?.reportCollationTypeKey,
                    resolveGuid(
                      defaultDelivery?.reportCollationTypeKey,
                      DEFAULT_COLLATION_TYPE_KEY,
                    ),
                  ),
                  createdUserKey: userKey,
                  tenantId,
                }),
              };
            }),
            {
              onSuccess: () => toast.success(t("toast.success.duplicatedItem")),
              onError: () => toast.error(t("toast.error.duplicateItemFailed")),
            },
          );
        }
        break;
    }
  };

  const handleLibraryAddToList = (
    node: ReportTreeItemData,
    targetReportList: ReportList,
  ) => {
    if (!node.reportKey) return;

    track("report_added_to_list", {
      report_key: node.reportKey,
      report_name: node.name,
    });

    const userKey = getUserKey();
    // Use the report list's default delivery settings if available
    const defaultDelivery = targetReportList.defaultReportDelivery;

    toast.promise(
      (async () => {
        const targetListItems =
          targetReportList.reportListKey === activeReportList?.reportListKey
            ? activeListItems
            : await queryClient.fetchQuery({
                queryKey: queryKeys.reportListItems.byList(
                  targetReportList.reportListKey,
                ),
                queryFn: () =>
                  reportListItemApi.getReportListItemsByListKey(
                    targetReportList.reportListKey,
                  ),
              });

        // Pre-populate with currently edited library parameters when available,
        // otherwise fetch defaults from the API.
        let serializedParams: string;
        const shouldUseActiveLibraryParameters =
          selectedReportNode?.reportKey === node.reportKey &&
          activeReportParameters.length > 0;

        if (shouldUseActiveLibraryParameters) {
          const entries = parametersToEntries(activeReportParameters);
          serializedParams = serializeParameters(entries);
        } else {
          try {
            if (node.path) {
              // Pass context parameters when fetching to ensure cascading
              // parameters like ComponentIDs get correct values
              const contextParams = {
                ProjectId: activeProjectId.toString(),
              };
              const params = await reportApi.getReportParameters(
                node.path,
                contextParams,
              );
              const entries = parametersToEntries(params);
              serializedParams = serializeParameters(entries);
            } else {
              serializedParams = serializeParameters([]);
            }
          } catch {
            // Fall back to empty parameters if fetch fails
            serializedParams = serializeParameters([]);
          }
        }

        await createMultipleItemsMutation.mutateAsync([
          {
            reportListItem: {
              reportListKey: targetReportList.reportListKey,
              reportKey: node.reportKey!,
              displayName: node.name,
              displayOrder:
                targetListItems.length === 0
                  ? 0
                  : Math.max(...targetListItems.map((item) => item.displayOrder)) + 1,
              reportListItemParameters: sanitizeParameterXml(serializedParams),
              createdUserKey: userKey,
              tenantId,
            },
            reportDelivery: createDefaultDeliveryDto({
              reportDeliveryTypeKey: resolveGuid(
                defaultDelivery?.reportDeliveryTypeKey,
                DEFAULT_DELIVERY_TYPE_KEY,
              ),
              reportFileTypeKey: resolveGuid(
                defaultDelivery?.reportFileTypeKey,
                DEFAULT_FILE_TYPE_KEY,
              ),
              reportCollationTypeKey: resolveGuid(
                defaultDelivery?.reportCollationTypeKey,
                DEFAULT_COLLATION_TYPE_KEY,
              ),
              createdUserKey: userKey,
              tenantId,
            }),
          },
        ]);

        // Wait for the items cache to be refreshed, then select the new item
        await queryClient.refetchQueries({
          queryKey: queryKeys.reportListItems.byList(targetReportList.reportListKey),
        });
        const freshItems = queryClient.getQueryData<ReportListItem[]>(
          queryKeys.reportListItems.byList(targetReportList.reportListKey),
        );
        // The new item is the one with the highest displayOrder
        const newItem = freshItems?.reduce<ReportListItem | null>(
          (best, item) => (!best || item.displayOrder > best.displayOrder ? item : best),
          null,
        );
        if (
          newItem &&
          targetReportList.reportListKey === activeReportList?.reportListKey
        ) {
          // Hydrate with parameters we already have
          const hydratedItem: ReportListItem = {
            ...newItem,
            report: {
              reportKey: node.reportKey!,
              name: node.name,
              path: node.path || "",
              tenantId,
              parameters: shouldUseActiveLibraryParameters
                ? activeReportParameters
                : undefined,
            } as Report,
          };
          setActiveContext("reportList");
          setFocusedListItem(hydratedItem);
          setSelectedReportNode(null);
        }
      })(),
      {
        loading: `Adding "${node.name}" to list...`,
        success: `Added "${node.name}" to list`,
        error: `Failed to add "${node.name}" to list`,
      },
    );
  };

  const handleLibraryPreviewReport = (node: ReportTreeItemData) => {
    if (!node.reportKey || !node.path) return;

    try {
      // Pass ALL current parameter values to ensure proper cascading in preview.
      // This matches Blazor's behavior: parameters are serialized with their current
      // values (including context params applied by applyContextParameters).
      // Reports with cascading parameters that depend on parent values beyond
      // just ProjectId require this complete context.
      // Infrastructure parameters are excluded by serializeParametersToJson.
      //
      // FALLBACK: If parameters haven't been fetched yet (array is empty),
      // pass at least the context parameters so preview doesn't fail on first click.
      let parametersJson: string;
      if (activeReportParameters.length > 0) {

        // Convert parameters to JSON dictionary, excluding infrastructure parameters
        const dict: Record<string, string> = {};
        for (const param of activeReportParameters) {
          if (EXCLUDED_PDF_PARAMS.has(param.name)) {
            continue;
          }
          dict[param.name] = getParameterStringValue(param);
        }
        parametersJson = JSON.stringify(dict);
      } else {
        // Parameters not loaded yet, fall back to context parameters only
        const contextOnlyParams = {
          ProjectId: activeProjectId.toString(),
        };
        parametersJson = JSON.stringify(contextOnlyParams);
      }

      // Build preview URL with parameters
      const params = new URLSearchParams();
      params.append("ReportKey", node.reportKey);
      params.append("ReportPath", node.path);
      params.append("Parameters", parametersJson);
      params.append("Source", "library");

      const previewUrl = `/ReportPreview?${params.toString()}`;


      // Check URL length and warn if too long
      const urlWarning = validateUrlLength(previewUrl, t);
      if (urlWarning) {
        console.warn(urlWarning);
        toast.warning(urlWarning, { duration: 5000 });
      }

      window.open(previewUrl, "_blank");
    } catch (error) {
      console.error("Failed to open library preview:", error);
      toast.error(t("toast.error.previewFailed"));
    }
  };

  const handleLibrarySavePdfReport = async (node: ReportTreeItemData) => {
    if (!node.path) return;
    await downloadPdfs(
      [
        {
          displayName: node.name,
          reportKey: node.reportKey || "",
          report: {
            reportKey: node.reportKey || "",
            name: node.name,
            path: node.path,
            tenantId: 0,
            parameters: activeReportParameters,
          },
        },
      ],
      "library",
    );
  };

  const handleToggleReportLibrary = () => {
    setReportLibraryExpanded(!reportLibraryExpanded);
  };

  const cascadeParameters = async (
    parameter: Parameter,
    updatedParams: Parameter[],
    reportPath: string,
  ): Promise<Parameter[]> => {
    const descendants = collectDescendantNames(
      parameter.childParameters,
      updatedParams,
    );
    const values = buildParameterDict(
      updatedParams.filter((p) => !descendants.has(p.name)),
    );

    const refreshed = await reportApi.getReportParameters(reportPath, values);
    const resolved = applyContextParameters(refreshed);

    // Preserve the user's selection — Telerik returns null for multi-value params.
    return resolved.map((p) =>
      p.name === parameter.name ? { ...p, ...parameter } : p,
    );
  };

  const persistParametersToListItem = async (finalParameters: Parameter[]) => {
    if (activeContext !== "reportList" || !singleSelectedListItem) return;

    const userKey = getUserKey();
    const serializedParams = serializeParameters(
      parametersToEntries(finalParameters),
    );
    const itemDelivery = singleSelectedListItem.reportDelivery;

    await updateReportListItemMutation.mutateAsync({
      reportListItem: {
        reportListItemKey: singleSelectedListItem.reportListItemKey,
        reportListKey: singleSelectedListItem.reportListKey,
        reportKey: singleSelectedListItem.reportKey,
        reportDeliveryKey: singleSelectedListItem.reportDeliveryKey,
        displayName: singleSelectedListItem.displayName,
        displayOrder: singleSelectedListItem.displayOrder,
        reportListItemParameters: serializedParams,
        lastModifiedUserKey: userKey,
        tenantId,
      },
      reportDelivery: createDefaultDeliveryUpdateDto({
        reportDeliveryKey:
          itemDelivery?.reportDeliveryKey ||
          singleSelectedListItem.reportDeliveryKey ||
          EMPTY_GUID,
        reportDeliveryTypeKey: resolveGuid(
          itemDelivery?.reportDeliveryTypeKey,
          DEFAULT_DELIVERY_TYPE_KEY,
        ),
        reportFileTypeKey: resolveGuid(
          itemDelivery?.reportFileTypeKey,
          DEFAULT_FILE_TYPE_KEY,
        ),
        reportCollationTypeKey: resolveGuid(
          itemDelivery?.reportCollationTypeKey,
          DEFAULT_COLLATION_TYPE_KEY,
        ),
        createdUserKey: itemDelivery?.createdUserKey || userKey,
        lastModifiedUserKey: userKey,
        tenantId,
      }),
      _optimisticParameters: finalParameters,
      _reportPath:
        singleSelectedListItem.report?.path || activeReportPath || undefined,
    });

    setSelectedListItems((prev) =>
      prev.map((item) =>
        item.reportListItemKey === singleSelectedListItem.reportListItemKey
          ? {
              ...item,
              reportListItemParameters: serializedParams,
              report: item.report
                ? { ...item.report, parameters: finalParameters }
                : item.report,
            }
          : item,
      ),
    );
  };

  const handleParameterChange = async (
    parameter: Parameter,
    report: Report,
  ) => {
    const updatedParams = activeReportParameters.map((p) =>
      p.name === parameter.name ? parameter : p,
    );

    setActiveReportParameters(updatedParams);

    let finalParameters = updatedParams;

    if (parameter.hasChildParameters && parameter.childParameters.length > 0 && report.path) {
      try {
        const merged = await cascadeParameters(parameter, updatedParams, report.path);
        setActiveReportParameters(merged);
        finalParameters = merged;
      } catch (error) {
        console.error("Failed to cascade parameters:", error);
        toast.error(t("preview.error.failedToUpdateParameters"));
      }
    }

    try {
      await persistParametersToListItem(finalParameters);
      if (activeContext === "reportList" && singleSelectedListItem) {
        toast.success(t("toast.success.parameterUpdated"));
      }
    } catch (error) {
      console.error("Failed to update report list item parameters:", error);
      toast.error(t("toast.error.parameterFailed"));
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Version Info and SignalR Status (top-right) */}
      <div className="absolute right-4 top-2 z-50 flex items-center gap-4">
        <SignalRStatus isConnected={signalrConnected} />
        <ProfileMenu />
      </div>

      {/* Action Bar */}
      <ActionBar
        projects={projects}
        activeProjectId={activeProjectId}
        onProjectChange={(projectId) => {
          const project = projects.find((p) => p.projectId === projectId);
          trackEvent("ProjectSelected", {
            projectId: String(projectId),
            projectNumber: project?.projectNumber || "unknown",
          });
          track("project_selected", {
            project_id: projectId,
            project_number: project?.projectNumber || "unknown",
          });
          setActiveProjectId(projectId);
        }}
        canExecute={activeTabSelectedItems.length > 0}
        selectedCount={activeTabSelectedItems.length}
        onPreview={() => handlePreview()}
        onSavePdf={() => handleSavePdf()}
      />

      {/* Main Content Area */}
      {/* Main Content Area: 3-column layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Report Library (collapsible sidebar with animation) */}
        {!isMobile && (
          <>
            <div
              className={cn(
                "flex shrink-0 flex-col overflow-hidden border-r transition-all duration-200 ease-in-out",
                reportLibraryExpanded
                  ? "w-[20vw] min-w-64"
                  : "w-0 min-w-0 border-r-0",
              )}
            >
              <div className="flex h-full w-full flex-col overflow-hidden">
                <ReportLibrary
                  reportTreesByType={reportTreesByType}
                  expandedFoldersByType={expandedFoldersByType}
                  onExpandedFoldersByTypeChange={setExpandedFoldersByType}
                  selectedNode={selectedReportNode}
                  onNodeSelect={(node) => {
                    setSelectedReportNode(node);
                    setFocusedListItem(null);
                    setActiveContext("reportLibrary");
                  }}
                  onContextClick={() => setActiveContext("reportLibrary")}
                  activeReportList={activeReportList}
                  openReportLists={openTabs}
                  disabled={reportsLoading}
                  onAddToList={handleLibraryAddToList}
                  onPreviewReport={handleLibraryPreviewReport}
                  onSavePdfReport={handleLibrarySavePdfReport}
                  initialReportType={
                    requestedProjectId !== null ? "project" : undefined
                  }
                />
              </div>
            </div>

            {/* Left sidebar toggle */}
            <div className="flex flex-col items-center border-r">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-none"
                title={
                  reportLibraryExpanded
                    ? "Hide report library"
                    : "Show report library"
                }
                onClick={handleToggleReportLibrary}
              >
                {reportLibraryExpanded ? (
                  <PanelLeftClose className="h-4 w-4" />
                ) : (
                  <PanelLeftOpen className="h-4 w-4" />
                )}
              </Button>
            </div>
          </>
        )}

        {/* Center: Report List Section */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <ReportListSection
            openTabs={openTabs}
            activeTabIndex={activeTabIndex}
            onTabChange={(index) => {
              setActiveContext("reportList");
              setActiveTabIndex(index);
              setFocusedListItem(null);
            }}
            onCloseTab={handleCloseTab}
            onCreateClick={() => {
              setActiveContext("reportList");
              setCreateDialogOpen(true);
            }}
            onOpenClick={() => {
              setActiveContext("reportList");
              setOpenDialogOpen(true);
            }}
            onDuplicateClick={() => {
              setActiveContext("reportList");
              setDuplicateDialogOpen(true);
            }}
            onRenameClick={() => {
              setActiveContext("reportList");
              setRenameDialogOpen(true);
            }}
            onDeleteClick={() => {
              setActiveContext("reportList");
              setDeleteDialogOpen(true);
            }}
            onDeliveryConfigClick={() => {
              setActiveContext("reportList");
              setDeliveryDialogOpen(true);
            }}
            onTabRename={handleTabRename}
            selectedItems={activeTabSelectedItems}
            onSelectionChange={handleActiveTabSelectionChange}
            activeItem={focusedListItem}
            onActiveItemChange={handleActiveItemChange}
            onItemContextAction={async (command, item) => {
              setActiveContext("reportList");
              await handleItemContextAction(command, item);
            }}
            reportResponses={allReportsResponse}
          />
        </div>

        {/* Desktop: Right sidebar toggle + Parameter Panel */}
        {!isMobile && (
          <>
            <div className="flex flex-col items-center border-l">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-none"
                title={
                  detailsPanelExpanded
                    ? "Hide details panel"
                    : "Show details panel"
                }
                onClick={() => setDetailsPanelExpanded(!detailsPanelExpanded)}
              >
                {detailsPanelExpanded ? (
                  <PanelRightClose className="h-4 w-4" />
                ) : (
                  <PanelRightOpen className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* Right: Parameter Panel (collapsible with animation) */}
            <div
              className={cn(
                "flex shrink-0 flex-col overflow-hidden border-l transition-all duration-200 ease-in-out",
                detailsPanelExpanded
                  ? "w-[20vw] min-w-64"
                  : "w-0 min-w-0 border-l-0",
              )}
            >
              <div className="flex h-full w-full flex-col overflow-hidden">
                <div className="border-b p-3">
                  <h5 className="text-lg font-semibold">{t("parameters.title")}</h5>
                  {hasMultipleListSelection && (
                    <div className="mt-1 space-y-0.5">
                      <p className="text-sm font-semibold">
                        {t("reportDetails.multipleSelected", {
                          count: activeTabSelectedItems.length,
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("reportDetails.selectSingleToEdit")}
                      </p>
                    </div>
                  )}
                  {activeReport && (
                    <div className="mt-1 space-y-0.5">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-semibold">
                          {activeReport.displayName || activeReport.name}
                        </span>
                        <span className="shrink-0 text-sm font-semibold text-muted-foreground">
                          {activeContext === "reportLibrary"
                            ? t("reportDetails.reportLibrary")
                            : t("reportDetails.reportList")}
                        </span>
                      </div>
                      {activeReport.reportVersion && (
                        <p className="text-xs text-muted-foreground">
                          Version: {activeReport.reportVersion}
                        </p>
                      )}
                      {activeReport.description && (
                        <p className="line-clamp-3 text-xs text-muted-foreground">
                          {activeReport.description}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-auto">
                  <ParameterPanel
                    key={`params-${activeProjectId}-${activeReportPath}`}
                    report={activeReport}
                    onValueSet={handleParameterChange}
                  />
                  {thumbnailNode && (
                    <div className="border-t p-3">
                      <h5 className="text-lg font-semibold">
                        {t("reportDetails.previewThumbnail")}
                      </h5>
                      <div className="mt-2">
                        <ReportPreviewImage node={thumbnailNode} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Mobile: Parameter Sheet */}
        {isMobile && (
          <MobileParameterSheet
            open={detailsPanelExpanded}
            onOpenChange={setDetailsPanelExpanded}
            report={activeReport}
            onValueSet={handleParameterChange}
            activeContext={activeContext}
            selectedReportNode={thumbnailNode}
          />
        )}
      </div>

      {/* Dialogs */}
      <CreateReportListDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        isPending={createReportListMutation.isPending}
        onConfirm={handleCreateReportList}
      />
      <OpenReportListDialog
        open={openDialogOpen}
        onOpenChange={setOpenDialogOpen}
        availableLists={availableReportLists}
        onConfirm={handleOpenReportLists}
      />
      <DuplicateReportListDialog
        open={duplicateDialogOpen}
        onOpenChange={setDuplicateDialogOpen}
        sourceName={activeReportList?.name || ""}
        isPending={
          createReportListMutation.isPending ||
          createMultipleItemsMutation.isPending
        }
        onConfirm={handleDuplicateReportList}
      />
      <RenameReportListDialog
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
        currentName={activeReportList?.name || ""}
        isPending={updateReportListMutation.isPending}
        onConfirm={handleRenameReportList}
      />
      <DeleteReportListDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        reportListName={activeReportList?.name || ""}
        isPending={deleteReportListMutation.isPending}
        onConfirm={handleDeleteReportList}
      />
      <DeliveryConfigDialog
        open={deliveryDialogOpen}
        onOpenChange={setDeliveryDialogOpen}
        delivery={activeReportList?.defaultReportDelivery || null}
        onSave={handleDeliveryConfigSave}
      />
    </div>
  );
}
